/**
 * Pure orchestration for the "Sign in with Ledger" OAuth callback landing.
 * When the browser returns to `/auth/callback?code=…&state=…` after the
 * authorize → consent redirect, this decides — with every side effect
 * injected — what the app should do:
 *
 *   - `not-callback`  — the URL is not an OAuth callback; boot normally.
 *   - `error`         — the IdP redirected back with an `error` param (e.g.
 *                       `access_denied`); surface it and stay unauthenticated.
 *   - `authenticated` — the code exchange succeeded; the caller flips auth on.
 *   - `failed`        — the exchange itself threw (state mismatch, network,
 *                       malformed token response); surface it, stay signed out.
 *
 * Ported verbatim from `apps/costpro-web/src/lib/auth/handle_oauth_callback.ts`
 * — logic identical, only the imports differ (this file uses wagepro's own
 * `./oauth_pkce.js`). The React seam (`main.tsx`) wires this into an effect;
 * this file holds all the branching so it is unit-testable with no DOM.
 */
import {
  isOAuthCallbackUrl,
  getOAuthCallbackError,
  type PkceDeps,
} from './oauth_pkce.js';

export type OAuthCallbackResult =
  | { kind: 'not-callback' }
  | { kind: 'authenticated' }
  | { kind: 'error'; error: string }
  | { kind: 'failed'; error: string };

export interface HandleOAuthCallbackDeps {
  currentUrl: string;
  completeSignIn: (currentUrl: string, deps: PkceDeps) => Promise<void>;
  pkceDeps: PkceDeps;
  navigateHome: () => void;
}

export async function handleOAuthCallback(
  deps: HandleOAuthCallbackDeps,
): Promise<OAuthCallbackResult> {
  const { currentUrl, completeSignIn, pkceDeps, navigateHome } = deps;

  // An error-only redirect (`?error=access_denied`, no `code`) is NOT an
  // `isOAuthCallbackUrl` — that check requires a `code`. Test the error param
  // first so a denied/rejected consent surfaces as `error` instead of falling
  // through to `not-callback` and silently re-triggering the same authorize.
  const callbackError = getOAuthCallbackError(currentUrl);
  if (callbackError !== null) {
    return { kind: 'error', error: callbackError };
  }

  if (!isOAuthCallbackUrl(currentUrl)) {
    return { kind: 'not-callback' };
  }

  try {
    await completeSignIn(currentUrl, pkceDeps);
  } catch (err) {
    return {
      kind: 'failed',
      error: err instanceof Error ? err.message : String(err),
    };
  }

  navigateHome();
  return { kind: 'authenticated' };
}
